import { useCallback, useMemo } from 'react';

import { useGetDispatchInProgressInfiniteQuery, type DispatchInProgressTableData } from '@/entities/dispatch';
import { useProjectSiteParams, useUrlState } from '@/shared/lib';

import { DEFAULT_URL_STATE } from './constants';

/**
 * In-progress dispatches data hook — server-paged infinite query + server
 * sorting, mirroring `useReceiveHistory`. Sorting state lives in the URL
 * (`sortBy` / `sortDir`, backend casing ASC/DESC; the DataTable speaks
 * lowercase).
 */
export const useDispatchInProgress = () => {
  const { projectId, siteId, isValid } = useProjectSiteParams();
  const [urlState, setUrlState] = useUrlState(DEFAULT_URL_STATE);

  const queryParams = useMemo(
    () => ({
      projectId,
      siteId,
      ...urlState
    }),
    [projectId, siteId, urlState]
  );

  const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, error, refetch } =
    useGetDispatchInProgressInfiniteQuery(queryParams, {
      skip: !isValid
    });

  const tableData = useMemo<DispatchInProgressTableData[]>(() => {
    if (!data?.pages) return [];

    return data.pages.flatMap((page) => page.content).map((row) => ({ ...row, id: row.journeyId }));
  }, [data]);

  const lastPage = data?.pages?.[data.pages.length - 1];
  const totalElements = lastPage?.totalElements || 0;

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSort = useCallback(
    (sortBy: string = DEFAULT_URL_STATE.sortBy, sortDirection: 'asc' | 'desc' = 'asc') => {
      setUrlState({
        sortBy,
        sortDir: sortDirection.toUpperCase()
      });
    },
    [setUrlState]
  );

  return {
    // Data
    tableData,
    totalElements,

    // Loading states
    isLoading: isLoading || (isFetching && !isFetchingNextPage),
    isFetchingNextPage,
    error,
    isValid,

    // Pagination
    hasMore: hasNextPage || false,
    handleLoadMore,

    // Sorting (DataTable casing)
    sortBy: urlState.sortBy,
    sortDirection: urlState.sortDir.toLowerCase() as 'asc' | 'desc',
    handleSort,

    refetch
  };
};
